import {Box, Flex} from '@chakra-ui/react';
import {useDailyLunchHome} from 'api/queries/product/useDailyLunch';
import {DishSkeleton, ScrollingMenu, ScrollingMenuCard, Section, SpecialProviderProduct} from 'components/shared';
import {useDesktop, useTranslation} from 'hooks';
import {useRouter} from 'next/router';
import React, {useCallback, useMemo} from 'react';
import logger from 'utils/logger';

const skeletonArray = new Array(8).fill(1);

export const DishLunchSlider = React.memo(() => {
  logger.logDebug(':rerender', 'DishLunchSlider');
  const {push} = useRouter();
  const {t} = useTranslation();
  const isDesktop = useDesktop();
  const {data, isLoading, isFetched} = useDailyLunchHome();

  const handleClick = useCallback(
    (product: any) => {
      push(`/restaurant/${product.provider_slug}#product-${product.product_id}`);
    },
    [push],
  );

  const childrens = useMemo(() => {
    if (!data?.length) return [];

    return data.map((item) => {
      return (
        <Section
          key={item.name}
          title={item.name}
          bg="transparent"
          sx={{
            '.react-horizontal-scrolling-menu--item:last-child': {
              pr: '10px !important',
            },
            // https://github.com/chakra-ui/chakra-ui/issues/622
            // The pseudo class ":first-child" is potentially unsafe when doing server-side rendering. Try changing it to ":first-of-type".
            '.react-horizontal-scrolling-menu--item:first-of-type': {
              pl: '10px !important',
            },
          }}
          count={`${item.products.length}`}
          viewAllLabel={t('VIEW_ALL')}
          linkTo={`/products/${item.slug}`}
          onHeaderClick={() => push(`/products/${item.slug}`)}
          cursor="pointer"
        >
          {isLoading ? (
            <Flex overflow="hidden">
              <Flex gap={['15px', '20px']}>
                {skeletonArray.map((_, index) => (
                  <DishSkeleton key={index} />
                ))}
              </Flex>
            </Flex>
          ) : (
            <Box px={[0, '10px']}>
              <ScrollingMenu>
                {item.products.map((specialProduct) => (
                  <ScrollingMenuCard itemId={`${specialProduct.product_id}`} key={specialProduct.product_id}>
                    <Box w={['180px', '320px']} h="100%" pr={['10px', '30px']}>
                      <SpecialProviderProduct
                        onClick={handleClick}
                        product={specialProduct}
                        isFromSlider={true}
                        isDesktop={isDesktop}
                      />
                    </Box>
                  </ScrollingMenuCard>
                ))}
              </ScrollingMenu>
            </Box>
          )}
        </Section>
      );
    });
  }, [data, isDesktop, isLoading, t, push, handleClick]);

  if (!data?.length) return null;
  if (isFetched && !data?.length) return null;
  if (data.map((item) => item.products.length).reduce((a, b) => a + b, 0) === 0) return null;

  return <Box>{childrens}</Box>;
});
